/**
 * Web IntroScreen Component
 *
 * Intro screen with Via Logos title and begin button
 *
 * Pure React - no react-native dependencies
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../services/ThemeContext';
import SettingsIconButton from './Helpers/SettingsIconButton.web';

interface IntroScreenProps {
  onBegin: () => void;
  onOpenSettings: () => void;
}

const IntroScreen: React.FC<IntroScreenProps> = ({ onBegin, onOpenSettings }) => {
  const { currentTheme: colors } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 400);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    // Trigger animation
    setTimeout(() => setIsVisible(true), 100);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBegin = () => {
    onBegin();
  };

  // Responsive font sizes
  const isSmallScreen = width < 400;
  const isMediumScreen = width < 768;

  const titleFontSize = isSmallScreen ? 40 : isMediumScreen ? 50 : 60;
  const hookFontSize = isSmallScreen ? 18 : 20;

  // Helper to darken color by percentage
  const darkenColor = (color: string, percent: number): string => {
    let hex = color.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    r = Math.floor((r * (100 - percent)) / 100);
    g = Math.floor((g * (100 - percent)) / 100);
    b = Math.floor((b * (100 - percent)) / 100);
    const toHex = (n: number) => {
      const h = n.toString(16);
      return h.length === 1 ? '0' + h : h;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  return (
    <div style={{
      flex: 1,
      maxWidth: 500,
      width: '100%',
      margin: '0 auto',
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: 40,
    }}>
      {/* Settings button - top right */}
      <SettingsIconButton
        onClick={onOpenSettings}
        data-testid="intro-settings-button"
        style={{ position: 'absolute', top: 20, right: 20 }}
      />

      {/* Title and Hook - centered */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        paddingBottom: 80,
      }}>
        <div style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(300px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
          textAlign: 'center',
        }}>
          <h1 style={{
            fontFamily: 'EagleLake, Arial, sans-serif',
            fontWeight: 400,
            fontStyle: 'normal',
            textAlign: 'center',
            textShadow: '4px -1.5px 6px rgba(212, 175, 55, 0.5)',
            marginBottom: 12,
            color: '#4a90e2',
            fontSize: `${titleFontSize}px`,
            lineHeight: 1.2,
            margin: 0,
          }}>
            Via Logos
          </h1>

          <p style={{
            fontFamily: 'EagleLake, Arial, sans-serif',
            fontWeight: 400,
            fontStyle: 'italic',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(153, 153, 153, 0.5)',
            color: '#e0e0e0',
            fontSize: `${hookFontSize}px`,
            margin: 0,
            marginTop: 12,
          }}>
            Walk through the living Word.
          </p>
        </div>
      </div>

      {/* Begin Button - stays at bottom */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: 40,
      }}>
        <button
          data-testid="intro-begin-button"
          onClick={handleBegin}
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(300px)',
            transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            backgroundColor: darkenColor(colors.primary, 20),
            color: '#fff',
            fontSize: isSmallScreen ? 16 : 18,
            fontWeight: 'bold',
            padding: '16px 48px',
            borderRadius: 30,
            border: 'none',
            cursor: 'pointer',
            minWidth: 200,
            textTransform: 'uppercase',
            letterSpacing: 2,
          }}
        >
          Begin
        </button>
      </div>
    </div>
  );
};

export default IntroScreen;
